// editor-panels.tsx - studio panels: viewport toolbar, bottom dock (assets + console), status bar, game setup
// Exports: ViewportToolbar, BottomDock, EditorStatusBar, winConditionSeedRules,
//          GameRulesPanel, GameCameraPanel, GameWorldPanel, GameMetaPanel

import { Fragment, useEffect, useLayoutEffect, useMemo, useRef, useState, useSyncExternalStore } from 'react';
import { createPortal } from 'react-dom';
import type { KeyboardEvent as ReactKeyboardEvent, PointerEvent as ReactPointerEvent, ReactElement, ReactNode } from 'react';
import { narrowGrantedCapabilities, type StudioCapabilityName } from '../../engine/scripting/capabilityNames';
import { buildScriptModules } from '../../engine/scripting/scriptModules';
import { useScriptStore } from '../scripting/scriptStore';
import { version as ENGINE_VERSION } from '../../../package.json';
import { ObjectThumbnail } from '../../engine/editor/thumbnails/ObjectThumbnail';
import { ObjectBrowser } from '../../engine/tools/object-browser/ObjectBrowser';
import { TabStrip, panelId, type TabDescriptor } from '../../engine/editor/components/Tabs';
import { M2Label } from './panelPrimitives';
import { formatDiagnostic, formatScriptValue } from './scriptConsoleFormat';
import {
  getObjectDefinition,
  getObjectRegistryVersion,
  LIBRARY_ID_PREFIX,
  lightFieldsFromAssetId,
  listObjectDefinitions,
  PREFABS,
  selectionToPrefab,
  subscribeObjectRegistry,
  useMapEditorStore,
  usePrefabStore,
  type CharacterDefinition,
  type MapFloorLevel,
  type MapLight,
  type MapObjectInstance,
  type PrefabDefinition,
} from '../../engine';
import { useEngineRuntimeStore } from '../../engine/store/engineRuntimeStore';
import { useScriptConsoleStore } from './scriptConsoleStore';
import { HollowScriptEditor } from './scripting/monaco/HollowScriptEditor';
import { useTheme } from '../design/theme';
import { Link } from '../design/router';
import { toast } from '../design/toast';
import { saveMapDocument, saveProjectConfig } from '../persistence/projectRepository';
import { useProject } from '../project/ProjectProvider';
import { cameraModeFromProjection, tilemapModeFromProjection, GAMESETUP_DOC_FALLBACK, type GameSetupDoc } from '../services/gameSetup';
import { LOGIC_DOCUMENT_VERSION, type AuthoredLogicRule, type LogicDocV2 } from './logicContract';
import { useEditorHost } from './EditorHost';
import { useSharedModeDocument } from './panel-system/sharedModeDocument';

/** Empty, not `LOGIC_DOC_FALLBACK` (Logic mode's own starter demo rules,
 * `editor-modes2.tsx`'s `LOGIC_RULES`) - reading the logic document from
 * here must never invent rules that were never actually saved. Only
 * `applyWinConditionPreset` below writes to this document, and only rules
 * it adds itself. */
const EMPTY_LOGIC_DOC: LogicDocV2 = { version: LOGIC_DOCUMENT_VERSION, rules: [] };

/** Reserved rule-id prefix for rules `applyWinConditionPreset` seeds, so a
 * later preset change can find and replace exactly its own previous rules
 * without touching anything the creator authored themselves. */
const WIN_CONDITION_RULE_PREFIX = '__winCondition-';

/** The leading number in a free-text field like Session length's "100s" -
 * same convention `clampNumericField` (below) already parses, reused here
 * as seconds. Falls back to `fallback` when nothing numeric is present. */
function leadingNumber(raw: string, fallback: number): number {
  const match = raw.match(/^\s*(-?\d*\.?\d+)/);
  if (!match) return fallback;
  const n = Number(match[1]);
  return Number.isFinite(n) && n > 0 ? n : fallback;
}

/**
 * Starter Logic rules for a Game Setup win-condition preset, built from
 * existing runtime primitives alone (SF23 quest/win action kinds - see
 * contract.ts). Only two presets have a generic template safe to guess at
 * blind - "Score target" and "Survival time" both need just a number this
 * panel already collects (`winsNeeded`, `sessionLength`). The others
 * ("Objective complete", "Last standing", "Race finish") depend on
 * something this panel cannot know (which trigger zone is the objective,
 * multiplayer elimination state) - seeding a rule for those would be
 * guessing at the creator's map, so they get none: the Logic-mode hint
 * below still points there, same as "Custom (Logic rule)" always has.
 */
export function winConditionSeedRules(condition: string, sessionLength: string, winsNeeded: string): AuthoredLogicRule[] {
  if (condition === 'Score target') {
    const target = Math.round(leadingNumber(winsNeeded, 10));
    return [{
      id: WIN_CONDITION_RULE_PREFIX + 'score',
      name: 'Win: score target',
      when: 'tick',
      if: [{ fact: 'variable', name: 'score', op: 'ge', value: target }],
      then: [{ kind: 'win-game' }],
      once: true,
      enabled: true,
    }];
  }
  if (condition === 'Survival time') {
    const seconds = Math.round(leadingNumber(sessionLength, 100));
    return [
      {
        id: WIN_CONDITION_RULE_PREFIX + 'timer',
        name: 'Survival timer',
        when: 'tick',
        then: [{ kind: 'add-variable', name: 'elapsed', value: 1 }],
        enabled: true,
      },
      {
        id: WIN_CONDITION_RULE_PREFIX + 'survive',
        name: 'Win: survived',
        when: 'tick',
        if: [{ fact: 'variable', name: 'elapsed', op: 'ge', value: seconds }],
        then: [{ kind: 'win-game' }],
        once: true,
        enabled: true,
      },
    ];
  }
  return [];
}

/* ── viewport toolbar (floating, top-centre) ──────────────────────────── */
type VtKind = 'select' | 'move' | 'rotate' | 'scale';

export function VtIcon({ kind }: { kind: VtKind }) {
  const p: Record<VtKind, ReactElement> = {
    select: <path d="M3 2.2l9.4 5.2-4.2 1.1-2.2 4L3 2.2z" stroke="currentColor" strokeWidth="1.2" strokeLinejoin="round" fill="none" />,
    move: <path d="M8 1.5v13M1.5 8h13M8 1.5L6 3.5M8 1.5l2 2M8 14.5l-2-2M8 14.5l2-2M1.5 8l2-2M1.5 8l2 2M14.5 8l-2-2M14.5 8l-2 2" stroke="currentColor" strokeWidth="1.2" strokeLinecap="round" strokeLinejoin="round" fill="none" />,
    rotate: <path d="M13.5 8a5.5 5.5 0 11-1.6-3.9M13.5 1.5v3h-3" stroke="currentColor" strokeWidth="1.2" strokeLinecap="round" strokeLinejoin="round" fill="none" />,
    scale: <g stroke="currentColor" strokeWidth="1.2" strokeLinecap="round" strokeLinejoin="round" fill="none"><rect x="2.5" y="6.5" width="7" height="7" rx="1" /><path d="M9.5 6.5l4-4M13.5 5.5v-3h-3" /></g>,
  };
  return <svg width="15" height="15" viewBox="0 0 16 16" aria-hidden="true">{p[kind]}</svg>;
}

/** The store's gizmo mode for each toolbar tool; 'select' turns the gizmo off. */
const VT_GIZMO: Record<VtKind, 'off' | 'translate' | 'rotate' | 'scale'> = {
  select: 'off', move: 'translate', rotate: 'rotate', scale: 'scale',
};

/** Visibility category labels shown in the filter popover. */
const VIS_LABELS: Record<'props' | 'lights' | 'triggers' | 'markers' | 'decals', string> = {
  props: 'Props', lights: 'Lights', triggers: 'Triggers', markers: 'Markers', decals: 'Decals',
};
type VisCategory = keyof typeof VIS_LABELS;
const VIS_CATEGORIES = Object.keys(VIS_LABELS) as VisCategory[];

/**
 * One section of a viewport bar. Sections are laid out left to right with a
 * hairline divider between each; when the bar is too narrow for all of
 * them, the rightmost non-pinned sections move into an overflow popover -
 * Unreal 5.6's viewport-toolbar behaviour ("elements collapse into overflow
 * menus when space is limited"), driven by a ResizeObserver on the bar
 * rather than a window media query, because a docked viewport's width
 * depends on the panel layout around it, not on the window.
 */
export interface ViewportBarSection {
  /** Stable key, also the measurement-cache key. */
  id: string;
  node: ReactNode;
  /** Never moves into the overflow menu (the primary tool palette - losing
   * Select/Move/Rotate/Scale behind a menu would be a worse trade than any
   * amount of crowding). */
  pinned?: boolean;
}

const BAR_H_PADDING = 16;
const BAR_GAP = 8;
const SEPARATOR_W = 9;
const OVERFLOW_TRIGGER_W = 38;

/**
 * Decides which sections fit. Split out from the component (and exported)
 * so the packing rule is testable without a live layout - see
 * tests/unit/viewportBarOverflow.test.ts. `widths` may be missing an entry
 * for a section that has never been rendered inline; such a section is
 * treated as its `fallbackWidth`, which keeps a cold start conservative
 * rather than optimistically overflowing the bar.
 */
export function packViewportBar(
  sections: readonly { id: string; pinned?: boolean }[],
  widths: ReadonlyMap<string, number>,
  available: number,
  fallbackWidth = 96,
): Set<string> {
  const widthOf = (id: string) => widths.get(id) ?? fallbackWidth;
  const total = sections.reduce((sum, s, i) => sum + widthOf(s.id) + (i > 0 ? BAR_GAP + SEPARATOR_W : 0), 0);
  if (total <= available) return new Set();

  // Something must overflow, so the trigger's own width is now part of the
  // budget for everything that stays.
  const budget = available - OVERFLOW_TRIGGER_W - BAR_GAP;
  const overflow = new Set<string>();
  let used = 0;
  let placed = 0;
  for (const section of sections) {
    const cost = widthOf(section.id) + (placed > 0 ? BAR_GAP + SEPARATOR_W : 0);
    // A pinned section is placed whatever the cost - it is the one thing
    // that must never hide - but it still consumes budget, so the sections
    // after it collapse sooner rather than the bar silently overflowing.
    if (section.pinned || used + cost <= budget) {
      used += cost;
      placed += 1;
    } else {
      overflow.add(section.id);
    }
  }
  return overflow;
}

/**
 * A docked toolbar row for a viewport. Rendered as a real flex row directly
 * above the canvas, never as an overlay floating on top of it: the previous
 * floating-pill toolbar sized itself purely from its own content and, at
 * Scene's own measured column widths, painted 79px over the scene tree on
 * one side and 79px over the Inspector on the other. A docked row cannot
 * overlap a sibling panel however wide its contents want to be.
 */
export function ViewportBar({ sections }: { sections: readonly ViewportBarSection[] }) {
  const barRef = useRef<HTMLDivElement | null>(null);
  const itemRefs = useRef(new Map<string, HTMLElement>());
  const naturalRef = useRef(new Map<string, number>());
  const [overflowIds, setOverflowIds] = useState<ReadonlySet<string>>(() => new Set());
  const [menuOpen, setMenuOpen] = useState(false);

  // Section identity, not the nodes themselves - the nodes change on every
  // render (fresh closures), the layout question does not.
  const sectionKey = sections.map((s) => `${s.id}:${s.pinned ? 1 : 0}`).join('|');

  useLayoutEffect(() => {
    const bar = barRef.current;
    if (!bar) return;
    const shape = sections.map((s) => ({ id: s.id, pinned: s.pinned }));
    const remeasure = () => {
      // A zero-width bar tells us nothing: dockview creates a panel's DOM
      // before giving it a size, so packing against 0 here would collapse
      // everything but the pinned group for the frame between creation and
      // the first real layout, which reads as the toolbar visibly
      // rearranging itself every time a Scene tab is opened.
      if (bar.clientWidth === 0) return;
      for (const [id, el] of itemRefs.current) {
        if (!el.isConnected) continue;
        const width = el.getBoundingClientRect().width;
        // Only ever grow the cached natural width: a section currently
        // inside the overflow popover is laid out under different
        // constraints, and a section mid-transition can measure as 0.
        if (width > (naturalRef.current.get(id) ?? 0)) naturalRef.current.set(id, width);
      }
      const next = packViewportBar(shape, naturalRef.current, bar.clientWidth - BAR_H_PADDING);
      setOverflowIds((prev) => (prev.size === next.size && [...next].every((id) => prev.has(id)) ? prev : next));
    };
    remeasure();
    const observer = new ResizeObserver(remeasure);
    observer.observe(bar);
    return () => observer.disconnect();
    // eslint-disable-next-line react-hooks/exhaustive-deps -- `sections` is a fresh array every render; sectionKey is its stable identity.
  }, [sectionKey]);

  useEffect(() => {
    if (!menuOpen) return;
    const close = (event: MouseEvent) => {
      if (!barRef.current?.contains(event.target as Node)) setMenuOpen(false);
    };
    document.addEventListener('mousedown', close);
    return () => document.removeEventListener('mousedown', close);
  }, [menuOpen]);

  // Nothing overflows any more (the viewport was widened) - drop the menu
  // rather than leaving an empty popover open over the canvas.
  useEffect(() => {
    if (overflowIds.size === 0 && menuOpen) setMenuOpen(false);
  }, [overflowIds, menuOpen]);

  const setItemRef = (id: string) => (el: HTMLElement | null) => {
    if (el) itemRefs.current.set(id, el);
    else itemRefs.current.delete(id);
  };

  const inline = sections.filter((s) => !overflowIds.has(s.id));
  const hidden = sections.filter((s) => overflowIds.has(s.id));

  // The clipping track and the popover are deliberately siblings: the track
  // must clip (a section mid-measurement, or one whose natural width is not
  // yet known, would otherwise paint past the bar and back over the
  // neighbouring panel - the exact failure this bar replaced), but the
  // popover has to escape that clip to drop down over the viewport.
  return (
    <div ref={barRef} className="ed-viewport-bar">
      <div className="ed-viewport-bar-track">
        {inline.map((section, index) => (
          <Fragment key={section.id}>
            {index > 0 && <span className="ed-viewport-bar-sep" aria-hidden="true" />}
            <div ref={setItemRef(section.id)} className="ed-viewport-bar-group">{section.node}</div>
          </Fragment>
        ))}
        {hidden.length > 0 && (
          <>
            <span style={{ flex: 1, minWidth: 0 }} />
            <button
              className={'ed-vt-btn' + (menuOpen ? ' is-active' : '')}
              onClick={() => setMenuOpen((open) => !open)}
              aria-expanded={menuOpen}
              aria-label={`More viewport controls (${hidden.length} hidden)`}
              title={`More viewport controls (${hidden.length} hidden)`}
            >
              <svg width="15" height="15" viewBox="0 0 16 16" aria-hidden="true">
                <g fill="currentColor"><circle cx="3" cy="8" r="1.4" /><circle cx="8" cy="8" r="1.4" /><circle cx="13" cy="8" r="1.4" /></g>
              </svg>
            </button>
          </>
        )}
      </div>
      {hidden.length > 0 && menuOpen && (
        <div className="ed-vt-overflow" role="menu">
          {hidden.map((section) => (
            <div key={section.id} className="ed-viewport-bar-group">{section.node}</div>
          ))}
        </div>
      )}
    </div>
  );
}

/** An icon tool button in a viewport bar (transform tools, the overflow
 * trigger). */
export function VtToolButton({ active, label, onClick, children }: { active?: boolean; label: string; onClick: () => void; children: ReactNode }) {
  return (
    <button className={'ed-vt-btn' + (active ? ' is-active' : '')} onClick={onClick} title={label} aria-label={label} aria-pressed={active}>
      {children}
    </button>
  );
}

/** A labelled switch in a viewport bar (SNAP, GRID, FLY). */
export function VtSwitch({ label, on, onChange, hint }: { label: string; on: boolean; onChange: () => void; hint?: string }) {
  return (
    <span className="ed-vt-switch" title={hint}>
      <button role="switch" aria-checked={on} className={'ss-switch' + (on ? ' is-on' : '')} onClick={onChange} aria-label={label} />
      <span>{label}</span>
    </span>
  );
}

/** A text chip in a viewport bar - either a toggle (VIS), a read-only badge
 * (the 2D/3D dimension) or a disabled placeholder (LIT). */
export function VtChip({ on, label, hint, onClick, disabled, icon }: {
  on?: boolean; label: string; hint?: string; onClick?: () => void; disabled?: boolean; icon?: ReactNode;
}) {
  return (
    <button
      className={'ed-vt-chip' + (on ? ' is-on' : '') + (onClick ? '' : ' is-static')}
      onClick={onClick}
      disabled={disabled}
      title={hint}
      aria-pressed={onClick ? Boolean(on) : undefined}
    >
      {icon}
      {label}
    </button>
  );
}

/**
 * Edit mode's viewport bar: transform tools, snapping, camera, the floor
 * stepper, layer visibility and the view mode - grouped and ordered the way
 * Unreal's own level viewport toolbar groups them (transform/snap, then
 * camera, then show-flags, then view options), so the bar reads as
 * categories rather than a flat row of unrelated controls.
 */
export function ViewportToolbar({ dim, showGrid = false, onToggleGrid }: { dim: string; showGrid?: boolean; onToggleGrid?: () => void }) {
  const gizmoMode = useMapEditorStore((state) => state.gizmoMode);
  const setGizmoMode = useMapEditorStore((state) => state.setGizmoMode);
  const snapMode = useMapEditorStore((state) => state.snapMode);
  const setSnapMode = useMapEditorStore((state) => state.setSnapMode);
  const freeCamMode = useMapEditorStore((state) => state.freeCamMode);
  const setFreeCamMode = useMapEditorStore((state) => state.setFreeCamMode);
  const setDevFlyMode = useEngineRuntimeStore((state) => state.setDevFlyMode);
  const availableFloors = useMapEditorStore((state) => state.availableFloors);
  const visibilityToggles = useMapEditorStore((state) => state.visibilityToggles);
  const setVisibilityToggle = useMapEditorStore((state) => state.setVisibilityToggle);
  const currentFloor = useEngineRuntimeStore((state) => state.currentFloor);
  const setCurrentFloor = useEngineRuntimeStore((state) => state.setCurrentFloor);

  const snap = snapMode === 'grid';
  // The engine is Y-up with an X-Z ground plane (see e.g. MapWallSegment's
  // own `rotationY` field): in a 2D top-down scene only Y-rotation ("facing"
  // in the ground plane) and X/Z-scale (footprint) have any visible effect;
  // the tooltip says so rather than offering the full 3-axis gizmo with no
  // indication most of it does nothing to a flat scene.
  const tools: [VtKind, string][] = dim === '2D'
    ? [['select', 'Select · V'], ['move', 'Move · G'], ['rotate', 'Rotate · R (Y-axis only affects a 2D scene)'], ['scale', 'Scale · S (X/Z only affect a 2D scene)']]
    : [['select', 'Select · V'], ['move', 'Move · G'], ['rotate', 'Rotate · R'], ['scale', 'Scale · S']];

  // Floor switcher helpers.
  const sortedFloors = [...availableFloors].sort((a, b) => a - b);
  const floorIndex = sortedFloors.indexOf(currentFloor);
  const canPrev = floorIndex > 0;
  const canNext = floorIndex < sortedFloors.length - 1;
  const floorLabel = availableFloors.length === 0 ? 'Floor 0' : `Floor ${currentFloor}`;

  const stepFloor = (dir: -1 | 1) => {
    const next = sortedFloors[floorIndex + dir];
    if (next !== undefined) setCurrentFloor(next);
  };

  // Visibility popover.
  const [showVis, setShowVis] = useState(false);
  const visRef = useRef<HTMLDivElement | null>(null);
  useEffect(() => {
    if (!showVis) return;
    const handler = (e: MouseEvent) => {
      if (visRef.current && !visRef.current.contains(e.target as Node)) setShowVis(false);
    };
    document.addEventListener('mousedown', handler);
    return () => document.removeEventListener('mousedown', handler);
  }, [showVis]);

  const anyHidden = VIS_CATEGORIES.some((c) => !visibilityToggles[c]);

  const toggleFly = () => {
    const next = !freeCamMode;
    setFreeCamMode(next);
    setDevFlyMode(next);
  };

  const sections: ViewportBarSection[] = [
    {
      id: 'transform',
      pinned: true,
      node: tools.map(([id, label]) => (
        <VtToolButton key={id} active={gizmoMode === VT_GIZMO[id]} label={label} onClick={() => setGizmoMode(VT_GIZMO[id])}>
          <VtIcon kind={id} />
        </VtToolButton>
      )),
    },
    {
      id: 'snap',
      node: (
        <>
          <VtSwitch label="SNAP" on={snap} onChange={() => setSnapMode(snap ? 'off' : 'grid')} hint="Toggle grid snap" />
          {onToggleGrid !== undefined && dim !== '2D' && (
            <VtSwitch label="GRID" on={showGrid} onChange={onToggleGrid} hint={showGrid ? 'Hide 3D grid' : 'Show 3D grid'} />
          )}
        </>
      ),
    },
    ...(dim !== '2D'
      ? [{
        id: 'camera',
        node: (
          <VtSwitch
            label="FLY"
            on={freeCamMode}
            onChange={toggleFly}
            hint={freeCamMode ? 'Fly mode ON - WASD + Space/Shift to move, click to exit' : 'Fly mode - WASD + mouse to navigate the viewport'}
          />
        ),
      }]
      : []),
    {
      id: 'floor',
      node: (
        <div style={{ display: 'flex', alignItems: 'center', gap: 2 }}>
          <button
            className="ed-vt-btn"
            style={{ width: 22 }}
            onClick={() => stepFloor(-1)}
            disabled={!canPrev}
            aria-label="Previous floor"
            title={canPrev ? `Go to floor ${sortedFloors[floorIndex - 1]}` : 'Already on the lowest floor'}
          >
            <svg width="11" height="11" viewBox="0 0 16 16" aria-hidden="true"><path d="M10 3L5 8l5 5" stroke="currentColor" strokeWidth="1.6" strokeLinecap="round" strokeLinejoin="round" fill="none" /></svg>
          </button>
          <span
            title={`Active floor: ${currentFloor}. Multi-floor maps switch here.`}
            style={{ fontFamily: 'var(--ss-sans)', fontWeight: 600, fontSize: 'var(--ss-fs-3xs)', letterSpacing: '0.08em', color: 'var(--ss-text-2)', whiteSpace: 'nowrap', minWidth: 46, textAlign: 'center' }}
          >{floorLabel}</span>
          <button
            className="ed-vt-btn"
            style={{ width: 22 }}
            onClick={() => stepFloor(1)}
            disabled={!canNext}
            aria-label="Next floor"
            title={canNext ? `Go to floor ${sortedFloors[floorIndex + 1]}` : 'Already on the highest floor'}
          >
            <svg width="11" height="11" viewBox="0 0 16 16" aria-hidden="true"><path d="M6 3l5 5-5 5" stroke="currentColor" strokeWidth="1.6" strokeLinecap="round" strokeLinejoin="round" fill="none" /></svg>
          </button>
        </div>
      ),
    },
    {
      id: 'visibility',
      node: (
        <div ref={visRef} style={{ position: 'relative' }}>
          <VtChip
            on={anyHidden}
            label="VIS"
            hint="Toggle scene layer visibility"
            onClick={() => setShowVis((v) => !v)}
            icon={
              <svg width="13" height="13" viewBox="0 0 16 16" fill="none" aria-hidden="true">
                <ellipse cx="8" cy="8" rx="6.5" ry="4" stroke="currentColor" strokeWidth="1.3" />
                <circle cx="8" cy="8" r="1.8" fill="currentColor" />
                {anyHidden && <line x1="2" y1="14" x2="14" y2="2" stroke="currentColor" strokeWidth="1.4" strokeLinecap="round" />}
              </svg>
            }
          />
          {showVis && (
            <div style={{
              position: 'absolute', top: 'calc(100% + 6px)', left: 0,
              background: 'var(--ss-bg-2)', border: '1px solid var(--ss-border-soft)', borderRadius: 10, padding: '8px 6px',
              boxShadow: '0 12px 32px rgba(0,0,0,0.32)',
              display: 'flex', flexDirection: 'column', gap: 2, minWidth: 132, zIndex: 40,
            }}>
              {VIS_CATEGORIES.map((cat) => {
                const on = visibilityToggles[cat];
                return (
                  <button
                    key={cat}
                    onClick={() => setVisibilityToggle(cat, !on)}
                    style={{
                      display: 'flex', alignItems: 'center', gap: 8, padding: '5px 10px', borderRadius: 7,
                      border: 'none', background: 'transparent', color: on ? 'var(--ss-text)' : 'var(--ss-text-3)',
                      fontFamily: 'var(--ss-sans)', fontWeight: 500, fontSize: 'var(--ss-fs-sm)', letterSpacing: '0.02em', textAlign: 'left',
                      cursor: 'pointer',
                    }}
                  >
                    <span style={{
                      width: 14, height: 14, borderRadius: 4, flexShrink: 0,
                      border: '1.5px solid ' + (on ? 'var(--ss-accent)' : 'var(--ss-border)'),
                      background: on ? 'var(--ss-accent)' : 'transparent',
                      display: 'flex', alignItems: 'center', justifyContent: 'center',
                    }}>
                      {on && <svg width="8" height="8" viewBox="0 0 10 10" fill="none" aria-hidden="true"><path d="M1.5 5L4 7.5L8.5 2.5" stroke="white" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round" /></svg>}
                    </span>
                    {VIS_LABELS[cat]}
                  </button>
                );
              })}
            </div>
          )}
        </div>
      ),
    },
    {
      id: 'view',
      node: (
        <>
          <VtChip on label={dim} hint="Game dimension. Set at creation, changeable in Game Setup." />
          <VtChip label="LIT" disabled hint="Wireframe and unlit view modes arrive with the engine profiler" />
        </>
      ),
    },
  ];

  return <ViewportBar sections={sections} />;
}

/* ── shared confirm dialog ────────────────────────────────────────────── */
/** Studio-styled confirmation for destructive actions (Enter confirms, Escape cancels). */
export function EdConfirmDialog({ title, body, confirmLabel, onConfirm, onCancel }: {
  title: string; body: string; confirmLabel: string; onConfirm: () => void; onCancel: () => void;
}) {
  useEffect(() => {
    const handler = (event: KeyboardEvent) => {
      if (event.key === 'Escape') { event.stopPropagation(); onCancel(); }
      if (event.key === 'Enter') { event.stopPropagation(); onConfirm(); }
    };
    // Capture phase so the dialog wins over the editor shortcut bindings.
    window.addEventListener('keydown', handler, true);
    return () => window.removeEventListener('keydown', handler, true);
  }, [onCancel, onConfirm]);
  return (
    <div onClick={onCancel} style={{ position: 'fixed', inset: 0, zIndex: 90, background: 'rgba(4,4,6,0.6)', display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
      <div onClick={(e) => e.stopPropagation()} role="dialog" aria-modal="true" aria-label={title} style={{
        width: 360, background: 'var(--ss-bg-2)', border: '1px solid var(--ss-border)', borderRadius: 14,
        padding: '20px 22px 18px', boxShadow: '0 30px 80px rgba(0,0,0,0.55)',
      }}>
        <h3 style={{ fontFamily: 'var(--ss-display)', fontSize: 17, fontWeight: 500, letterSpacing: '-0.01em', marginBottom: 8 }}>{title}</h3>
        <p style={{ fontSize: 12.5, color: 'var(--ss-text-2)', lineHeight: 1.55, marginBottom: 18 }}>{body}</p>
        <div style={{ display: 'flex', justifyContent: 'flex-end', gap: 8 }}>
          <button className="ss-btn ss-btn--ghost" style={{ padding: '7px 14px', fontSize: 12.5, borderRadius: 9 }} onClick={onCancel}>Cancel</button>
          <button className="ss-btn ss-btn--danger-solid" style={{ padding: '7px 14px', fontSize: 12.5, borderRadius: 9 }} onClick={onConfirm} autoFocus>{confirmLabel}</button>
        </div>
      </div>
    </div>
  );
}

/**
 * The typed-confirmation sibling of EdConfirmDialog, for the handful of
 * catastrophic, hard-to-reverse actions (delete project, delete account,
 * delete a publication) that ask for a specific typed phrase rather than a
 * plain yes/no - the same severity-scaled pattern those actions already
 * used via window.prompt, now in the app's own styled dialog instead of a
 * native browser prompt.
 */
export function EdTypedConfirmDialog({ title, body, confirmLabel, requiredText, onConfirm, onCancel }: {
  title: string; body: string; confirmLabel: string; requiredText: string; onConfirm: () => void; onCancel: () => void;
}) {
  const [typed, setTyped] = useState('');
  const valid = typed === requiredText;
  useEffect(() => {
    const handler = (event: KeyboardEvent) => {
      if (event.key === 'Escape') { event.stopPropagation(); onCancel(); }
      if (event.key === 'Enter' && valid) { event.stopPropagation(); onConfirm(); }
    };
    // Capture phase so the dialog wins over the editor shortcut bindings.
    window.addEventListener('keydown', handler, true);
    return () => window.removeEventListener('keydown', handler, true);
  }, [onCancel, onConfirm, valid]);
  return (
    <div onClick={onCancel} style={{ position: 'fixed', inset: 0, zIndex: 90, background: 'rgba(4,4,6,0.6)', display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
      <div onClick={(e) => e.stopPropagation()} role="dialog" aria-modal="true" aria-label={title} style={{
        width: 380, background: 'var(--ss-bg-2)', border: '1px solid var(--ss-border)', borderRadius: 14,
        padding: '20px 22px 18px', boxShadow: '0 30px 80px rgba(0,0,0,0.55)',
      }}>
        <h3 style={{ fontFamily: 'var(--ss-display)', fontSize: 17, fontWeight: 500, letterSpacing: '-0.01em', marginBottom: 8 }}>{title}</h3>
        <p style={{ fontSize: 12.5, color: 'var(--ss-text-2)', lineHeight: 1.55, marginBottom: 12 }}>{body}</p>
        <label style={{ display: 'block', fontSize: 11.5, color: 'var(--ss-text-3)', marginBottom: 6 }}>
          Type <strong style={{ color: 'var(--ss-text)', fontFamily: 'var(--ss-mono)' }}>{requiredText}</strong> to confirm
        </label>
        <input
          className="ss-input"
          autoFocus
          value={typed}
          onChange={(e) => setTyped(e.target.value)}
          style={{ width: '100%', padding: '8px 12px', fontSize: 13, borderRadius: 8, marginBottom: 16 }}
        />
        <div style={{ display: 'flex', justifyContent: 'flex-end', gap: 8 }}>
          <button className="ss-btn ss-btn--ghost" style={{ padding: '7px 14px', fontSize: 12.5, borderRadius: 9 }} onClick={onCancel}>Cancel</button>
          <button
            className="ss-btn ss-btn--danger-solid"
            disabled={!valid}
            style={{ padding: '7px 14px', fontSize: 12.5, borderRadius: 9, opacity: valid ? 1 : 0.45, cursor: valid ? 'pointer' : 'default' }}
            onClick={onConfirm}
          >
            {confirmLabel}
          </button>
        </div>
      </div>
    </div>
  );
}

/* ── bottom dock: Assets + HollowScript console ───────────────────────── */
export function DockAssets({ searchSlot }: { searchSlot: HTMLElement | null }) {
  const { project, projectId } = useEditorHost();
  const is2d = project.config.projection === '2D';
  // 3D projects no longer have a Core pack tab (its content lives inside
  // Library now, as the pinned Soulstack Originals author) - Library is the
  // right default there. 2D still has Core pack (Library is 3D-only), and
  // the effect below immediately flips that further to Sprites anyway, so
  // this initial value only ever actually renders for 3D.
  const [filter, setFilter] = useState(() => (is2d ? 'Core pack' : 'Library'));
  const [query, setQuery] = useState('');
  const pendingAssetId = useMapEditorStore((state) => state.pendingAssetId);
  const setPendingAssetId = useMapEditorStore((state) => state.setPendingAssetId);
  // A 2D creator's most useful palette is what they paint in Pixel Studio -
  // jump to Sprites before the first paint so there is no flash of Core pack
  // first. useLayoutEffect (not useEffect) specifically so this beats the
  // browser's paint.
  useLayoutEffect(() => {
    if (is2d) setFilter('Sprites');
    // eslint-disable-next-line react-hooks/exhaustive-deps -- once, on mount, matching the "no flash" intent; a later projection change is a full project switch (new component instance) anyway.
  }, []);
  // The registry mutates as uploads and the library manifest register.
  const registryVersion = useSyncExternalStore(subscribeObjectRegistry, getObjectRegistryVersion);
  // The live registry: core pack definitions minus the project's uploads and
  // the shared library. 3D no longer has its own Core pack tab (folded into
  // Library's pinned Soulstack Originals entry - see the ObjectBrowser call
  // below), so this is 2D-only work now; skip it entirely for 3D.
  const uploadIds = useMemo(() => new Set(project.assets.map((a) => a.id)), [project.assets]);
  const core = useMemo(
    () => {
      if (!is2d) return [];
      void registryVersion;
      return listObjectDefinitions()
        .filter((d) => !uploadIds.has(d.id) && !d.id.startsWith(LIBRARY_ID_PREFIX))
        // A 2D scene never renders 3D geometry (LevelRenderer isn't part of
        // the 2D runtime path), so structural/furniture/fixture/effects
        // props would sit in the palette doing nothing; only gameplay
        // triggers and markers carry data a 2D project's HUD/Logic can
        // still read regardless of projection.
        .filter((d) => d.category === 'gameplay')
        .map((d) => ({ id: d.id, label: d.id }));
    },
    [uploadIds, registryVersion, is2d],
  );
  const uploads = useMemo(() => project.assets.map((a) => ({ id: a.id, label: a.name })), [project.assets]);
  // The sprite library authored in Pixel Studio (saved into the 2D layer).
  const sprites = useMemo(() => {
    const spriteList = project.map.tilemap2d?.sprites
      ?? (project.map.tilemap2d?.playerSprite ? [project.map.tilemap2d.playerSprite] : []);
    return spriteList.map((s) => ({
      id: s.id,
      label: s.id + ((s.frames?.length ?? 0) > 0 ? ' · ' + (1 + s.frames!.length) + 'f' : ''),
    }));
  }, [project.map.tilemap2d]);
  // The Library tab renders ObjectBrowser directly instead (its own search,
  // category/author/bundle facets and hover previews over the full 5k+
  // shared registry) - see the render branch below, and its `pinOriginals`
  // prop: Core pack's own items are registered with `editor.author ===
  // 'Soulstack Originals'`, so Library presents them as a pinned, always-
  // expanded author entry (same treatment any other Library author gets,
  // just first and never collapsed) instead of 3D projects needing a
  // separate Core pack tab at all. 2D keeps Core pack as its own tab below -
  // Library is 3D-only, so that redundancy never existed there. Prefabs
  // (both the built-in preset catalogue and a project's own saved ones) live
  // entirely under the dock's own top-level Prefabs tab now (DockPrefabs) -
  // see that component's history for why having a second, differently-
  // behaved "Prefabs" here was a real point of confusion, not just a
  // styling gap.
  const all = filter === 'Core pack' ? core : filter === 'Uploads' ? uploads : sprites;
  const items = filter === 'Library' ? [] : all.filter((a) => a.label.toLowerCase().includes(query.trim().toLowerCase()));
  // Sprites are painted in Pixel Studio into tilemap2d, a 2D-only document -
  // a 3D project can never populate it, so offering the tab there was a
  // guaranteed-empty dead end rather than a real category. 3D has no Core
  // pack tab either now - see the ObjectBrowser call's `pinOriginals` prop.
  const filterOptions = is2d ? ['Core pack', 'Uploads', 'Sprites'] : ['Library', 'Uploads'];
  const importTile = (
    <Link to={'/app/project/' + projectId + '/assets'} title="Upload models and audio on the project Assets page" className="ss-asset-tile ss-asset-tile--import">
      <span className="ss-asset-tile-plus">+</span>
      <span className="ss-asset-tile-label">IMPORT</span>
    </Link>
  );
  return (
    <div style={{ display: 'flex', minHeight: 0, flex: 1 }}>
      {searchSlot && createPortal(
        <input className="ss-input ss-input--compact" placeholder="Search assets" value={query} onChange={(e) => setQuery(e.target.value)} style={{ width: 150 }} />,
        searchSlot,
      )}
      <div style={{ width: 125, flexShrink: 0, minHeight: 0, borderRight: '1px solid var(--ss-border-soft)', padding: '12px 12px', display: 'flex', flexDirection: 'column', gap: 6 }}>
        <M2Label style={{ padding: '0 4px', marginTop: 0, marginBottom: 0 }}>Category</M2Label>
        <TabStrip
          listLabel="Asset category"
          variant="rail"
          className="ss-asset-category-rail"
          tabs={filterOptions.map((f): TabDescriptor => ({ id: f, label: f }))}
          activeId={filter}
          onSelect={setFilter}
          // Nested inside this dock's own sidebar (which already supplies
          // width/border/padding) - cancel out the rail variant's own copy
          // of that same box, same as Account settings' rail. flex +
          // minHeight: 0 lets the list itself scroll (via .ss-asset-
          // category-rail's overflow-y) rather than pushing the dock taller
          // once there are more categories than the dock has room for. The
          // sidebar's own 125px width (rather than a round 120/130) is
          // tuned so its right border lines up with the tab row's own
          // Assets|Prefabs divider above it - see the BottomDock tab row
          // this sits directly under.
          style={{ width: 'auto', padding: 0, border: 'none', background: 'transparent', flex: '1 1 auto', minHeight: 0, overflowY: 'auto' }}
        />
      </div>
      {filter === 'Library' ? (
        <div className="ss-dock-library" style={{ flex: 1, minWidth: 0, minHeight: 0 }}>
          <ObjectBrowser
            searchQuery={query}
            onSearchQueryChange={setQuery}
            selectedAssetId={pendingAssetId}
            onSelectAsset={(id) => setPendingAssetId(pendingAssetId === id ? null : id)}
            searchInputClassName="ss-input ss-input--compact"
            hideHeader
            pinOriginals
          />
        </div>
      ) : (
      <div className="ss-asset-grid" style={{ flex: 1, minWidth: 0, minHeight: 0, display: 'flex', flexWrap: 'wrap', alignContent: 'flex-start', gap: 8, padding: '10px' }}>
        {items.length === 0 ? (
          // A flex-wrap row puts the message and the import tile side by
          // side whenever there's room, which read as two unrelated
          // controls floating next to each other rather than "here's why
          // it's empty, here's what to do about it" - stack them instead
          // so the tile reads as the message's own call to action.
          <div style={{ display: 'flex', flexDirection: 'column', alignItems: 'flex-start', gap: 14 }}>
            <span style={{ fontSize: 12, color: 'var(--ss-text-3)', padding: '12px 4px 0' }}>
              {filter === 'Uploads' ? 'No uploads yet. Add a .glb from the project Assets page.' : filter === 'Sprites' ? 'No sprites yet. Paint one in Pixel Studio and save to assets.' : 'Nothing matches.'}
            </span>
            {importTile}
          </div>
        ) : (
          <>
            {items.map((a) => {
              // Only model-typed tabs have registry entries with rendered thumbnails.
              // ('Library' never reaches here - it takes the branch above.)
              const hasThumb = filter === 'Core pack' || filter === 'Uploads';
              const isPending = pendingAssetId === a.id;
              return (
                <button
                  key={a.id}
                  title={isPending ? 'Click in viewport to place · Escape to cancel' : 'Click to select for placement, or drag into viewport'}
                  draggable={hasThumb}
                  onDragStart={(e) => e.dataTransfer.setData('text/plain', a.id)}
                  onClick={() => setPendingAssetId(isPending ? null : a.id)}
                  className={'ss-asset-tile' + (isPending ? ' is-pending' : '')}>
                  {hasThumb ? (
                    <ObjectThumbnail assetId={a.id} label={a.label} size={50} />
                  ) : (
                    <span className="ss-asset-tile-icon">
                      <svg width="16" height="16" viewBox="0 0 16 16" fill="none" aria-hidden="true"><path d="M8 1.8l5.5 3.1v6.2L8 14.2l-5.5-3.1V4.9L8 1.8zM2.5 4.9L8 8m0 0l5.5-3.1M8 8v6.2" stroke="currentColor" strokeWidth="1.2" strokeLinejoin="round" /></svg>
                    </span>
                  )}
                  <span className="ss-asset-tile-label">{a.label}</span>
                </button>
              );
            })}
            {importTile}
          </>
        )}
      </div>
      )}
    </div>
  );
}

type ConsoleLineKind = 'sys' | 'cmd' | 'ok' | 'err' | 'out';
interface ConsoleLine { k: ConsoleLineKind; t: string }

const CONSOLE_BANNER: ConsoleLine = { k: 'sys', t: `HollowScript console · engine ${ENGINE_VERSION} · type help` };

/** Narrows GameSetupDoc.scriptCapabilities' plain strings (task item 4's
 * per-project grant) down to real v1 capability names - now a thin alias
 * over the engine's own `narrowGrantedCapabilities`, the single rule every
 * script surface reads (the Code workspace, the Preview console, the play
 * surface and the editor's diagnostics), so `print`/`clock`/`random` are
 * always granted and `input` is grantable, exactly as at runtime.
 * `undefined` means "not configured": every v1 capability. */
export function narrowScriptCapabilities(allowed: readonly string[] | undefined): ReadonlySet<StudioCapabilityName> | undefined {
  return narrowGrantedCapabilities(allowed);
}

export function DockConsole() {
  const [lines, setLines] = useState<ConsoleLine[]>([CONSOLE_BANNER]);
  const [val, setVal] = useState('');
  const [running, setRunning] = useState(false);
  const endRef = useRef<HTMLDivElement | null>(null);
  const { theme } = useTheme();
  // Set by PreviewRoute (screens-editor.tsx) for the duration of a live play
  // session - null whenever no Preview is open, since there is nothing to
  // run a script against yet (no live liveSceneStore/ClientLogicRuntime).
  const runtime = useScriptConsoleStore((state) => state.runtime);
  const allowedCapabilities = useScriptConsoleStore((state) => state.allowedCapabilities);
  const sessionOutput = useScriptConsoleStore((state) => state.sessionOutput);
  useEffect(() => {
    const el = endRef.current;
    if (el && el.parentElement) el.parentElement.scrollTop = el.parentElement.scrollHeight;
  }, [lines]);
  // `print` lines from the session's own run-on-start scripts arrive via the
  // store (see scriptConsoleStore.sessionOutput); fold each new one into the
  // scrollback exactly once.
  const seenSessionOutput = useRef(0);
  useEffect(() => {
    const fresh = sessionOutput.filter((entry) => entry.id > seenSessionOutput.current);
    if (fresh.length === 0) return;
    seenSessionOutput.current = fresh[fresh.length - 1]!.id;
    setLines((l) => [...l, ...fresh.map((entry): ConsoleLine => ({ k: 'out', t: entry.text }))]);
  }, [sessionOutput]);

  async function run() {
    const source = val.trim();
    if (!source || running) return;
    const cmdLine: ConsoleLine = { k: 'cmd', t: source };
    if (source === 'help') {
      setLines((l) => [...l, cmdLine, { k: 'sys', t: 'stats · clear · or any HollowScript program (uses print, entities, variables, audio, logic, input, clock, random)' }]);
      setVal('');
      return;
    }
    if (source === 'clear') {
      setLines([CONSOLE_BANNER]);
      setVal('');
      return;
    }
    if (source === 'stats') {
      // The real scene, straight from the editor store.
      const state = useMapEditorStore.getState();
      const distinct = new Set(state.objectInstances.map((i) => i.assetId)).size;
      const floors = state.activeDocument?.floors.length ?? 1;
      setLines((l) => [...l, cmdLine, {
        k: 'ok',
        t: '✓ ' + state.objectInstances.length + ' objects · ' + distinct + ' distinct assets · ' + floors + (floors === 1 ? ' floor' : ' floors') + ' · ' + state.triggers.length + ' triggers',
      }]);
      setVal('');
      return;
    }
    if (!runtime) {
      setLines((l) => [...l, cmdLine, { k: 'err', t: 'No live Preview session - open Preview to run a script against a live scene.' }]);
      setVal('');
      return;
    }
    setLines((l) => [...l, cmdLine]);
    setVal('');
    setRunning(true);
    try {
      const outcome = await runtime.runScript(source, {
        capabilities: narrowScriptCapabilities(allowedCapabilities),
        onPrint: (text) => setLines((l) => [...l, { k: 'out', t: text }]),
        // The project's scripts are importable from a console line too.
        modules: buildScriptModules(useScriptStore.getState().scripts),
      });
      if (outcome.ok) {
        setLines((l) => [...l, { k: 'ok', t: '✓ ' + formatScriptValue(outcome.result) }]);
      } else {
        setLines((l) => [...l, ...outcome.diagnostics.map((d): ConsoleLine => ({ k: 'err', t: formatDiagnostic(d) }))]);
      }
    } finally {
      setRunning(false);
    }
  }
  const colour: Record<ConsoleLineKind, string> = { sys: 'var(--ss-text-3)', cmd: 'var(--ss-text)', ok: 'var(--ss-success)', err: 'var(--ss-danger)', out: 'var(--ss-text-2)' };
  return (
    <div style={{ display: 'flex', flexDirection: 'column', minHeight: 0, flex: 1 }}>
      <div style={{ flex: 1, overflowY: 'auto', padding: '10px 16px', fontFamily: 'var(--ss-mono)', fontSize: 11.5, lineHeight: 1.8 }}>
        {lines.map((l, i) => (
          <div key={i} style={{ color: colour[l.k], whiteSpace: 'pre-wrap' }}>
            {l.k === 'cmd' ? <span style={{ color: 'var(--ss-accent)' }}>» </span> : null}{l.t}
          </div>
        ))}
        <div ref={endRef} />
      </div>
      <div style={{ display: 'flex', alignItems: 'flex-end', gap: 10, padding: '8px 16px', borderTop: '1px solid var(--ss-border-soft)' }}>
        <span style={{ fontFamily: 'var(--ss-mono)', fontSize: 12, color: 'var(--ss-accent)', fontWeight: 600, paddingBottom: 3 }}>»</span>
        <div style={{ flex: 1 }}>
          <HollowScriptEditor
            value={val}
            onChange={setVal}
            onRunRequested={() => void run()}
            capabilities={narrowScriptCapabilities(allowedCapabilities)}
            theme={theme}
            readOnly={running}
            placeholder={runtime ? 'HollowScript source (Enter to run, Shift+Enter for a new line)' : 'HollowScript command (open Preview to run real scripts)'}
          />
        </div>
        <span className="ss-kbd">{running ? '…' : '↵'}</span>
      </div>
    </div>
  );
}

interface BottomDockProps {
  open: boolean;
  tab: string;
  setOpen: (open: boolean) => void;
  setTab: (tab: string) => void;
}

const BOTTOM_DOCK_TABS: TabDescriptor[] = [
  { id: 'assets', label: 'Assets' },
  { id: 'prefabs', label: 'Prefabs' },
  { id: 'console', label: 'Console' },
  { id: 'output', label: 'Output' },
  { id: 'profiler', label: 'Profiler' },
];

/** Collapsed row height - fixed, matches `.ed-tabstrip`'s own CSS height. */
const DOCK_COLLAPSED_HEIGHT = 30;
/** Open height a fresh session starts at, and the floor a drag-resize can't
 * go below - below this the Assets/Console content has no useful room. */
const DOCK_DEFAULT_HEIGHT = 192;
/** Drag-resize step for the ArrowUp/ArrowDown keyboard alternative. */
const DOCK_RESIZE_STEP = 24;

function dockMaxHeight(): number {
  // Leaves at least 30% of the viewport for the canvas/toolbar above -
  // dragging the dock open shouldn't be able to swallow the whole editor.
  return Math.max(DOCK_DEFAULT_HEIGHT, Math.round(window.innerHeight * 0.7));
}

export function BottomDock({ open, tab, setOpen, setTab }: BottomDockProps) {
  const [dockHeight, setDockHeight] = useState(DOCK_DEFAULT_HEIGHT);
  const [resizing, setResizing] = useState(false);
  const dragRef = useRef<{ startY: number; startHeight: number } | null>(null);
  const [searchSlot, setSearchSlot] = useState<HTMLDivElement | null>(null);

  const onResizePointerDown = (e: ReactPointerEvent<HTMLDivElement>) => {
    e.preventDefault();
    e.currentTarget.setPointerCapture(e.pointerId);
    dragRef.current = { startY: e.clientY, startHeight: dockHeight };
    setResizing(true);
  };
  const onResizePointerMove = (e: ReactPointerEvent<HTMLDivElement>) => {
    if (!dragRef.current) return;
    const next = dragRef.current.startHeight + (dragRef.current.startY - e.clientY);
    setDockHeight(Math.min(dockMaxHeight(), Math.max(DOCK_DEFAULT_HEIGHT, next)));
  };
  const onResizePointerUp = (e: ReactPointerEvent<HTMLDivElement>) => {
    dragRef.current = null;
    setResizing(false);
    e.currentTarget.releasePointerCapture(e.pointerId);
  };
  const onResizeKeyDown = (e: ReactKeyboardEvent<HTMLDivElement>) => {
    if (e.key === 'ArrowUp') { e.preventDefault(); setDockHeight((h) => Math.min(dockMaxHeight(), h + DOCK_RESIZE_STEP)); }
    else if (e.key === 'ArrowDown') { e.preventDefault(); setDockHeight((h) => Math.max(DOCK_DEFAULT_HEIGHT, h - DOCK_RESIZE_STEP)); }
  };

  return (
    <div style={{ flexShrink: 0, borderTop: '1px solid var(--ss-border-soft)', background: 'var(--ss-bg-2)', display: 'flex', flexDirection: 'column', height: open ? dockHeight : DOCK_COLLAPSED_HEIGHT, transition: resizing ? 'none' : 'height 220ms cubic-bezier(0.22,1,0.36,1)', overflow: 'hidden' }}>
      {open && (
        <div
          role="separator"
          aria-orientation="horizontal"
          aria-label="Resize dock"
          aria-valuenow={dockHeight}
          aria-valuemin={DOCK_DEFAULT_HEIGHT}
          aria-valuemax={dockMaxHeight()}
          tabIndex={0}
          className="ed-dock-resize-handle"
          onPointerDown={onResizePointerDown}
          onPointerMove={onResizePointerMove}
          onPointerUp={onResizePointerUp}
          onKeyDown={onResizeKeyDown}
        />
      )}
      <TabStrip
        listLabel="Editor bottom dock"
        tabs={BOTTOM_DOCK_TABS}
        activeId={tab}
        onSelect={(id) => { if (open && tab === id) { setOpen(false); } else { setTab(id); setOpen(true); } }}
        controlsPanels
        style={{ paddingRight: 8 }}
        after={
          <div style={{ display: 'flex', alignItems: 'center', gap: 8, marginLeft: 'auto' }}>
            <div ref={setSearchSlot} />
            <button onClick={() => setOpen(!open)} title={open ? 'Collapse dock' : 'Expand dock'} style={{
              border: 'none', background: 'transparent', color: 'var(--ss-text-3)', fontSize: 10, padding: '4px 8px', alignSelf: 'center',
            }}>{open ? '▾' : '▴'}</button>
          </div>
        }
      />
      {open && (
        <div id={panelId(tab)} style={{ display: 'contents' }}>
          {tab === 'assets' ? <DockAssets searchSlot={searchSlot} /> : tab === 'prefabs' ? <DockPrefabs searchSlot={searchSlot} /> : tab === 'console' ? <DockConsole /> : tab === 'output' ? <DockOutput /> : <DockProfiler />}
        </div>
      )}
    </div>
  );
}

/* ── status bar ───────────────────────────────────────────────────────── */
type OutputLineKind = 'info' | 'warn' | 'error';

export function DockOutput() {
  // The live scene, in place of an event log the engine does not emit yet.
  const instances = useMapEditorStore((state) => state.objectInstances);
  const triggers = useMapEditorStore((state) => state.triggers);
  const activeDocument = useMapEditorStore((state) => state.activeDocument);
  const saveState = useMapEditorStore((state) => state.saveState);
  const colours: Record<OutputLineKind, string> = { info: 'var(--ss-text-3)', warn: 'var(--ss-warning)', error: 'var(--ss-danger)' };
  const lines: [OutputLineKind, string][] = [
    ['info', 'Scene: ' + instances.length + ' objects · ' + (activeDocument?.floors.length ?? 1) + ' floor(s) · ' + triggers.length + ' trigger(s)'],
    ['info', 'Saves: ' + (saveState === 'saved' ? 'all changes saved' : saveState === 'autosaved' ? 'backed up, not saved to source - Ctrl+S to save' : saveState === 'saving' ? 'saving…' : saveState === 'error' ? 'last save failed' : 'no unsaved changes')],
  ];
  return (
    <div style={{ flex: 1, overflowY: 'auto', padding: '10px 16px', fontFamily: 'var(--ss-mono)', fontSize: 11, lineHeight: 1.9 }}>
      {lines.map(([k, msg], i) => (
        <div key={i} style={{ display: 'flex', gap: 12 }}>
          <span style={{ color: colours[k], fontWeight: 400, flexShrink: 0, width: 38, textTransform: 'uppercase', fontSize: 9.5, paddingTop: 2 }}>{k}</span>
          <span style={{ color: 'var(--ss-text-2)' }}>{msg}</span>
        </div>
      ))}
      <div style={{ marginTop: 8, color: 'var(--ss-text-3)', fontFamily: 'var(--ss-sans)', fontSize: 11 }}>
        Streaming engine events land here with a future HollowScript runtime hook.
      </div>
    </div>
  );
}

export function DockProfiler() {
  return (
    <div style={{ flex: 1, display: 'flex', alignItems: 'center', justifyContent: 'center', padding: '12px 18px' }}>
      <span style={{ fontSize: 11.5, color: 'var(--ss-text-3)', textAlign: 'center', lineHeight: 1.6, maxWidth: 420 }}>
        Frame, draw-call and memory budgets arrive with the engine profiler. Until then the status bar FPS counter is the live signal.
      </span>
    </div>
  );
}

const PREFAB_PRESET_CATEGORIES: TabDescriptor[] = [
  { id: 'all', label: 'All' },
  { id: 'tactical', label: 'Tactical' },
  { id: 'cover', label: 'Cover' },
  { id: 'lighting', label: 'Lighting' },
];

/** Converts a built-in preset's own lightweight instance shape into the real
 * `MapObjectInstance[]`/`MapLight[]` pair `insertPrefabGroup` expects, so a
 * preset drops through the exact same path as a saved prefab (one undo
 * step, ids minted by `makeObjectId`, not the bespoke per-instance
 * `addObject`/`updateObjectParam` loop this used to run) instead of a
 * second, slightly different mechanism. A `light_`-prefixed instance
 * becomes a real `MapLight` here, not a generic prop - lights render from
 * the scene's separate `lights` array, never from `objectInstances`, so
 * routing one through as a plain instance would silently place an inert,
 * unlit placeholder instead of an actual light. Light fields are derived by
 * `lightFieldsFromAssetId`, the same helper `addObject` (mapEditorStore.ts)
 * uses for live click-to-place, so a preset's light and a hand-placed one
 * of the same asset id get identical defaults. `id` on the returned
 * instances is a placeholder - `insertPrefabGroup` mints a fresh one for
 * every item regardless of what's passed in. Exported for direct unit
 * coverage (tests/unit/toDroppableGroup.test.ts) of the light/instance
 * split this function exists to get right. */
export function toDroppableGroup(def: PrefabDefinition): { instances: MapObjectInstance[]; lights: MapLight[] } {
  const instances: MapObjectInstance[] = [];
  const lights: MapLight[] = [];
  for (const inst of def.instances) {
    if (inst.assetId.startsWith('light_')) {
      lights.push({
        id: inst.assetId,
        floor: inst.floor,
        position: inst.transform.position,
        ...lightFieldsFromAssetId(inst.assetId, getObjectDefinition(inst.assetId), { color: inst.color, intensity: inst.intensity }),
      });
      continue;
    }
    const params: Record<string, unknown> = {};
    if (inst.color !== undefined) params.color = inst.color;
    if (inst.intensity !== undefined) params.intensity = inst.intensity;
    instances.push({
      id: inst.assetId,
      assetId: inst.assetId,
      floor: inst.floor,
      transform: inst.transform,
      name: inst.name,
      params: Object.keys(params).length > 0 ? params : undefined,
    });
  }
  return { instances, lights };
}

/**
 * Prefabs, in one place: browse the built-in preset catalogue (Tactical/
 * Cover/Lighting groups ready to drop in) and manage the project's own saved
 * groups (save the current selection, drop it back in anywhere, delete it).
 * These used to be two differently-behaved surfaces both called "Prefabs" -
 * this one (the built-in catalogue) under Assets, working by instant-spawn-
 * on-click with no search; the other (saved groups) here, working by an
 * explicit Drop button. Consolidated onto this tab, both a Drop button away
 * from the scene, both searchable from the same header box as every Assets
 * category. Persists with the project (EditorHost hydrates and autosaves it
 * alongside the rest of the docs store).
 */
export function DockPrefabs({ searchSlot }: { searchSlot: HTMLElement | null }) {
  const prefabs = usePrefabStore((s) => s.prefabs);
  const savePrefab = usePrefabStore((s) => s.savePrefab);
  const deletePrefab = usePrefabStore((s) => s.deletePrefab);

  const selectedObjectIds = useMapEditorStore((s) => s.selectedObjectIds);
  const objectInstances = useMapEditorStore((s) => s.objectInstances);
  const lights = useMapEditorStore((s) => s.lights);
  const triggers = useMapEditorStore((s) => s.triggers);
  const decals = useMapEditorStore((s) => s.decals);
  const insertPrefabGroup = useMapEditorStore((s) => s.insertPrefabGroup);
  const selectObject = useMapEditorStore((s) => s.selectObject);
  const lastRaycastHit = useMapEditorStore((s) => s.lastRaycastHit);
  const currentFloor = useEngineRuntimeStore((s) => s.currentFloor) as MapFloorLevel;

  const [saveName, setSaveName] = useState('');
  const [pendingDeleteId, setPendingDeleteId] = useState<string | null>(null);
  const [presetCategory, setPresetCategory] = useState('all');
  const [query, setQuery] = useState('');

  // Prefabs capture props/models, lights, triggers and decals - markers
  // (spawn points, checkpoints) live in their own collection the prefab
  // schema doesn't cover, since they're usually singular per level rather
  // than something worth duplicating as a group. selectionToPrefab is the
  // one shared place that pulls all four kinds out of the live entity
  // arrays (also used by the legacy MapEditorOverlay's own PrefabLibrary) -
  // hand-filtering each array here again is exactly how that other caller
  // once silently dropped lights/triggers/decals by only filtering
  // objectInstances. Count only the part of the selection that's actually
  // saveable so "N selected" can't promise more than Save delivers;
  // selecting only a marker correctly leaves this at 0.
  const selection = useMemo(
    () => selectionToPrefab({ objectInstances, lights, triggers, decals }, selectedObjectIds),
    [objectInstances, lights, triggers, decals, selectedObjectIds],
  );
  const selectedCount = selection.instances.length + selection.lights.length + selection.triggers.length + selection.decals.length;
  const nonPropSelectedCount = selectedObjectIds.length - selectedCount;

  const q = query.trim().toLowerCase();
  // Memoized: this dock re-renders on every `lastRaycastHit` update (the map
  // editor writes it on every pointer move over the viewport while this tab
  // is open), which would otherwise re-filter both lists dozens of times a
  // second for no reason - neither list depends on the cursor position.
  const presets = useMemo(
    () =>
      PREFABS.filter(
        (p) =>
          (presetCategory === 'all' || p.category === presetCategory) &&
          (!q || p.name.toLowerCase().includes(q) || p.description.toLowerCase().includes(q)),
      ),
    [presetCategory, q],
  );
  const savedPrefabs = useMemo(
    () => prefabs.filter((p) => !q || p.name.toLowerCase().includes(q)),
    [prefabs, q],
  );

  function handleSave() {
    if (selectedCount === 0) return;
    savePrefab(saveName || `Prefab ${prefabs.length + 1}`, selection);
    setSaveName('');
  }

  function dropGroup(group: Parameters<typeof insertPrefabGroup>[0], label: string) {
    const dropX = lastRaycastHit?.[0] ?? 0;
    const dropZ = lastRaycastHit?.[1] ?? 0;
    const newIds = insertPrefabGroup(group, currentFloor, [dropX, dropZ], label);
    if (newIds.length === 1) selectObject(newIds[0] ?? null);
  }

  function handleDropPreset(def: PrefabDefinition) {
    dropGroup(toDroppableGroup(def), 'Drop preset prefab');
    toast(`Dropped "${def.name}" (${def.instances.length} object${def.instances.length === 1 ? '' : 's'})`, 'success');
  }

  function handleDropSaved(prefab: (typeof prefabs)[number]) {
    dropGroup({ instances: prefab.instances, lights: prefab.lights, triggers: prefab.triggers, decals: prefab.decals }, 'Drop prefab');
  }

  const pendingDelete = pendingDeleteId ? prefabs.find((p) => p.id === pendingDeleteId) ?? null : null;

  return (
    <div style={{ flex: 1, display: 'flex', minHeight: 0 }}>
      {searchSlot && createPortal(
        <input className="ss-input ss-input--compact" placeholder="Search prefabs" value={query} onChange={(e) => setQuery(e.target.value)} style={{ width: 150 }} />,
        searchSlot,
      )}
      <div style={{ width: 220, flexShrink: 0, borderRight: '1px solid var(--ss-border-soft)', padding: '12px 12px', display: 'flex', flexDirection: 'column', gap: 8 }}>
        <M2Label style={{ padding: '0 4px', marginTop: 0, marginBottom: 0 }}>New prefab</M2Label>
        <input
          className="ss-input"
          placeholder="Prefab name…"
          value={saveName}
          onChange={(e) => setSaveName(e.target.value)}
          disabled={selectedCount === 0}
          style={{ padding: '7px 10px', fontSize: 12, borderRadius: 8 }}
        />
        <button
          className="ss-btn ss-btn--primary ss-btn--sm"
          onClick={handleSave}
          disabled={selectedCount === 0}
          title={selectedCount === 0 ? 'Select objects in the scene first' : `Save ${selectedCount} object${selectedCount > 1 ? 's' : ''} as a prefab`}
        >
          Save selection
        </button>
        <span style={{ fontSize: 11, color: 'var(--ss-text-3)', lineHeight: 1.5 }}>
          {selectedCount === 0 && nonPropSelectedCount === 0
            ? 'Select one or more props, lights, triggers or decals in the scene, then save them as a named group.'
            : selectedCount === 0
              ? "Prefabs can't include markers - select a prop, light, trigger or decal instead."
              : `${selectedCount} object${selectedCount > 1 ? 's' : ''} selected` +
                (nonPropSelectedCount > 0 ? ` (${nonPropSelectedCount} marker in the selection won't be saved).` : '.')}
        </span>
      </div>
      <div className="ss-prefab-list" style={{ flex: 1, overflowY: 'auto', padding: '10px 14px', display: 'flex', flexDirection: 'column', gap: 16 }}>
        <div style={{ display: 'flex', flexDirection: 'column', gap: 8 }}>
          <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', gap: 12 }}>
            <M2Label style={{ padding: 0, marginTop: 0, marginBottom: 0 }}>Presets</M2Label>
            <TabStrip
              listLabel="Preset category"
              variant="compact"
              tabs={PREFAB_PRESET_CATEGORIES}
              activeId={presetCategory}
              onSelect={setPresetCategory}
            />
          </div>
          {presets.length === 0 ? (
            <span style={{ fontSize: 12, color: 'var(--ss-text-3)', padding: '4px 4px 0' }}>Nothing matches.</span>
          ) : (
            <div style={{ display: 'flex', flexDirection: 'column', gap: 6 }}>
              {presets.map((def) => (
                <div
                  key={def.id}
                  onClick={() => handleDropPreset(def)}
                  className="ss-card"
                  title="Drop at cursor position"
                  style={{ padding: '8px 10px', display: 'flex', alignItems: 'center', gap: 10, cursor: 'pointer' }}
                >
                  <span style={{ fontSize: 20, flexShrink: 0 }}>{def.icon}</span>
                  <div style={{ flex: 1, minWidth: 0, display: 'flex', flexDirection: 'column', gap: 2 }}>
                    <span style={{ fontSize: 12.5, fontWeight: 600, color: 'var(--ss-text)' }}>{def.name}</span>
                    <span style={{ fontSize: 10.5, color: 'var(--ss-text-3)', lineHeight: 1.3, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>{def.description}</span>
                  </div>
                  <span
                    style={{
                      fontSize: 9.5, fontWeight: 600, color: 'var(--ss-success)',
                      border: '1px solid var(--ss-success-soft)', padding: '2px 5px', borderRadius: 999, flexShrink: 0,
                    }}
                  >
                    +{def.instances.length}
                  </span>
                </div>
              ))}
            </div>
          )}
        </div>
        <div style={{ display: 'flex', flexDirection: 'column', gap: 8 }}>
          <M2Label style={{ padding: 0, marginTop: 0, marginBottom: 0 }}>Your prefabs</M2Label>
          {savedPrefabs.length === 0 ? (
            <span style={{ fontSize: 12, color: 'var(--ss-text-3)', padding: '4px 4px 0' }}>
              {prefabs.length === 0 ? 'No prefabs saved yet.' : 'Nothing matches.'}
            </span>
          ) : (
            <div style={{ display: 'flex', flexDirection: 'column', gap: 6 }}>
              {savedPrefabs.map((prefab) => {
                const memberCount = prefab.instances.length + (prefab.lights?.length ?? 0) + (prefab.triggers?.length ?? 0) + (prefab.decals?.length ?? 0);
                return (
                  <div key={prefab.id} className="ss-card" style={{ padding: '8px 10px', display: 'flex', alignItems: 'center', gap: 10 }}>
                    <div style={{ flex: 1, minWidth: 0, display: 'flex', flexDirection: 'column', gap: 2 }}>
                      <span style={{ fontSize: 12.5, fontWeight: 600, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>{prefab.name}</span>
                      <span style={{ fontSize: 10.5, color: 'var(--ss-text-3)' }}>{memberCount} object{memberCount > 1 ? 's' : ''}</span>
                    </div>
                    <button className="ss-btn ss-btn--ghost ss-btn--sm" onClick={() => handleDropSaved(prefab)} title="Drop at cursor position">Drop</button>
                    <button
                      className="ss-btn ss-btn--ghost ss-btn--sm"
                      onClick={() => setPendingDeleteId(prefab.id)}
                      title="Delete this prefab"
                      style={{ color: 'var(--ss-danger)', borderColor: 'var(--ss-danger)' }}
                    >
                      Delete
                    </button>
                  </div>
                );
              })}
            </div>
          )}
        </div>
      </div>
      {pendingDelete && (
        <EdConfirmDialog
          title={`Delete "${pendingDelete.name}"?`}
          body="This permanently removes the saved prefab. Objects already placed in the scene from it are not affected."
          confirmLabel="Delete prefab"
          onConfirm={() => { deletePrefab(pendingDelete.id); setPendingDeleteId(null); }}
          onCancel={() => setPendingDeleteId(null)}
        />
      )}
    </div>
  );
}

export function EditorStatusBar({ onConsole, mode }: { onConsole: () => void; mode: string }) {
  const snapMode = useMapEditorStore((state) => state.snapMode);
  // A real frames-per-second readout; the design's chrome promised one.
  const [fps, setFps] = useState(60);
  useEffect(() => {
    let frames = 0;
    let last = performance.now();
    let raf = 0;
    const loop = (now: number) => {
      frames += 1;
      if (now - last >= 1000) {
        setFps(Math.round((frames * 1000) / (now - last)));
        frames = 0;
        last = now;
      }
      raf = requestAnimationFrame(loop);
    };
    raf = requestAnimationFrame(loop);
    return () => cancelAnimationFrame(raf);
  }, []);
  return (
    <div style={{
      height: 26, flexShrink: 0, display: 'flex', alignItems: 'center', justifyContent: 'space-between',
      padding: '0 12px', borderTop: '1px solid var(--ss-border-soft)', background: 'var(--ss-bg-2)',
      fontFamily: 'var(--ss-sans)', fontWeight: 500, fontSize: 9.5, letterSpacing: '0.08em', color: 'var(--ss-text-3)',
      whiteSpace: 'nowrap', textTransform: 'uppercase',
    }}>
      <div style={{ display: 'flex', gap: 16 }}>
        <span>ENGINE {ENGINE_VERSION}</span>
      </div>
      <div style={{ display: 'flex', gap: 16 }}>
        {/* The grid/snap readout only means anything on Edit's own viewport
            gizmo; every other mode (Map Builder has its own grid-snap field,
            Pixel Studio a flat pixel grid, everything else no canvas at all)
            was showing it unconditionally regardless of relevance. */}
        {mode === 'edit' && (
          <>
            <span>GRID 0.5M</span>
            <span>{snapMode === 'grid' ? 'SNAP ON' : 'SNAP OFF'}</span>
          </>
        )}
      </div>
      <div style={{ display: 'flex', gap: 14, alignItems: 'center', paddingRight: 92 }}>
        <span>{fps} FPS</span>
        <button onClick={onConsole} style={{
          border: 'none', background: 'transparent', color: 'var(--ss-text-2)', cursor: 'pointer',
          fontFamily: 'inherit', fontSize: 9, letterSpacing: '0.1em', display: 'flex', alignItems: 'center', gap: 6,
        }}>
          <span className="ss-kbd" style={{ fontSize: 9, padding: '0 5px' }}>`</span> CONSOLE
        </button>
      </div>
    </div>
  );
}

/* ── Game Setup mode ──────────────────────────────────────────────────── */
interface GsRadioOption { id: string; hint: string; soon?: boolean }

interface GsRadioCardsProps {
  options: GsRadioOption[];
  value: string;
  onChange: (id: string) => void;
  cols?: number;
}

export function GsRadioCards({ options, value, onChange, cols = 3 }: GsRadioCardsProps) {
  return (
    <div style={{ display: 'grid', gridTemplateColumns: 'repeat(' + cols + ', 1fr)', gap: 10 }}>
      {options.map((o) => (
        <button key={o.id} onClick={() => !o.soon && onChange(o.id)} disabled={o.soon} title={o.soon ? 'On the roadmap; not selectable yet' : undefined} style={{
          textAlign: 'left', padding: '14px 16px', borderRadius: 12,
          border: '1px solid ' + (value === o.id ? 'var(--ss-accent-border)' : 'var(--ss-border-soft)'),
          background: value === o.id ? 'var(--ss-accent-soft)' : 'transparent',
          color: 'var(--ss-text)', display: 'flex', flexDirection: 'column', gap: 5,
          opacity: o.soon ? 0.45 : 1,
        }}>
          <span style={{ fontSize: 13.5, fontWeight: 600, color: value === o.id ? 'var(--ss-accent)' : 'var(--ss-text)' }}>{o.id}{o.soon ? ' · soon' : ''}</span>
          <span style={{ fontSize: 11.5, color: 'var(--ss-text-3)', lineHeight: 1.45 }}>{o.hint}</span>
        </button>
      ))}
    </div>
  );
}

interface GsSegProps {
  options: string[];
  value: string;
  onChange: (option: string) => void;
  /** Human-readable label for the underlying tablist, announced to screen
   * readers (not shown visually) - name what the choice is for, e.g. "Team
   * format", "Project visibility". */
  listLabel: string;
}

export function GsSeg({ options, value, onChange, listLabel }: GsSegProps) {
  const tabs: TabDescriptor[] = options.map((o) => ({ id: o, label: o }));
  return (
    <TabStrip listLabel={listLabel} variant="segmented" tabs={tabs} activeId={value} onSelect={onChange} />
  );
}

interface GsToggleProps {
  label: string;
  hint?: string;
  on: boolean;
  onChange: (on: boolean) => void;
}

export function GsToggle({ label, hint, on, onChange }: GsToggleProps) {
  return (
    <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', gap: 16 }}>
      <div style={{ display: 'flex', flexDirection: 'column', gap: 2 }}>
        <span style={{ fontSize: 13.5, fontWeight: 500 }}>{label}</span>
        {hint && <span style={{ fontSize: 12, color: 'var(--ss-text-3)' }}>{hint}</span>}
      </div>
      <button role="switch" aria-checked={on} className={'ss-switch' + (on ? ' is-on' : '')} onClick={() => onChange(!on)} aria-label={label} />
    </div>
  );
}

/**
 * These rule fields store a free-text string with an optional trailing unit
 * (e.g. "100s", "3s") rather than a bare number, so they cannot simply become
 * `type="number"` inputs. Clamps the leading numeric portion to `min` on
 * blur - not on every keystroke, so typing itself stays unconstrained -
 * preserving whatever suffix followed it. Non-numeric input reverts to the
 * field's own previous value rather than being silently accepted.
 */
function clampNumericField(raw: string, previous: string, min = 0): string {
  const match = raw.match(/^\s*(-?\d*\.?\d+)(.*)$/);
  if (!match) return previous;
  const [, numStr, suffix] = match;
  const n = Number(numStr);
  if (!Number.isFinite(n)) return previous;
  return `${Math.max(min, n)}${suffix}`;
}

/** Re-derives which 2D camera choice is selected from Tilemap2D.mode itself
 * (the field this picker and Pixel Studio's own toggle both write), so a
 * mode change made in Pixel Studio shows up here too, not just the reverse.
 * Shared by game.camera and game.world (docs/editor-consolidation/
 * panel-content-spec.md §10) - both need the resolved projection label, not
 * just the raw stored '2D'/'3D' dimension. */
function resolvedProjection(storedProjection: string, tilemapMode: string | undefined): string {
  return storedProjection.startsWith('2D')
    ? (tilemapMode === 'platformer' ? '2D side view' : '2D top down')
    : storedProjection;
}

/** Roster entries the "Player character" picker offers: only ones with a
 * real imported rig (`importedGlbUrl`) are a viewable close-up first-person
 * body - a flat bone-transform-only or neutral-biped entry has no mesh of
 * its own to render as the player. Pulled out as a pure function for direct
 * testability, same convention as `computeBodyPickerState`
 * (editor-modes3.tsx). */
export function playerCharacterRosterOptions(characters: CharacterDefinition[]): CharacterDefinition[] {
  return characters.filter((c) => Boolean(c.importedGlbUrl));
}

/** Game type; Players & matchmaking; Sessions & winning; Spawning
 * (docs/editor-consolidation/panel-content-spec.md §10, `game.rules`). */
export function GameRulesPanel() {
  const { editorBase, project } = useEditorHost();
  const { data: storedDoc, setData: saveDoc } = useSharedModeDocument<GameSetupDoc>('gamesetup', GAMESETUP_DOC_FALLBACK);
  const playerCharacterOptions = useMemo(
    () => playerCharacterRosterOptions(project.characters),
    [project.characters],
  );
  // A second, independent shared mode document (see useSharedModeDocument's
  // own doc comment) - only touched by applyWinConditionPreset below, never
  // read into this panel's own form state.
  const { setData: saveLogicDoc } = useSharedModeDocument<LogicDocV2>('logic', EMPTY_LOGIC_DOC);
  const { type, teams, match, skill, sides } = storedDoc;
  const region = storedDoc.region ?? 'EU West';
  const sessionLength = storedDoc.sessionLength ?? '100s';
  const winsNeeded = storedDoc.winsNeeded ?? '7';
  const winCondition = storedDoc.winCondition ?? 'Objective complete';
  const multiplayer = type !== 'Solo';

  /**
   * Picking a win-condition preset seeds its starter Logic rule(s)
   * (`winConditionSeedRules`) into the project's real 'logic' document, so
   * the field actually drives runtime behaviour instead of sitting as a
   * label nothing reads. Idempotent and non-destructive: it only ever
   * replaces rules it previously seeded itself (matched by the reserved
   * `WIN_CONDITION_RULE_PREFIX`), so switching presets back and forth, or
   * picking a preset with no template ("Objective complete", "Last
   * standing", "Race finish", "Custom (Logic rule)" - see
   * `winConditionSeedRules`'s own doc comment for why), cleanly removes the
   * previous preset's rules without ever touching a rule the creator
   * authored or edited themselves in Logic mode.
   */
  function applyWinConditionPreset(condition: string, sessionLen: string, wins: string) {
    const seeded = winConditionSeedRules(condition, sessionLen, wins);
    saveLogicDoc((current) => ({
      ...current,
      rules: [...current.rules.filter((r) => !r.id.startsWith(WIN_CONDITION_RULE_PREFIX)), ...seeded],
    }));
  }

  return (
    <div style={{ flex: 1, overflowY: 'auto', minWidth: 0 }}>
      <div style={{ maxWidth: 760, margin: '0 auto', padding: '36px 40px 72px' }}>
        <div style={{ marginBottom: 26 }}>
          <h1 style={{ fontFamily: 'var(--ss-display)', fontSize: 24, fontWeight: 500, letterSpacing: '-0.02em' }}>Game setup</h1>
          <p style={{ fontSize: 13, color: 'var(--ss-text-3)', marginTop: 5 }}>Saved with your project. Camera and projection apply everywhere today; match rules apply as multiplayer hosting features land.</p>
        </div>
        <div style={{ display: 'flex', flexDirection: 'column', gap: 14 }}>
          <div className="set-card">
            <h3>Game type</h3>
            <GsRadioCards value={type} onChange={(v) => saveDoc((doc) => ({ ...doc, type: v }))} options={[
              { id: 'Solo', hint: 'Single player, no servers needed.' },
              { id: 'Co-op', hint: 'Small parties play together against the world.' },
              { id: 'Versus', hint: 'Teams compete. Server-authoritative, always.' },
            ]} />
            <span className="hint">Built for any genre: shooters, platformers, racers, puzzles, RPGs, sims. Game rules live in Logic.</span>
          </div>

          {multiplayer && (
            <div className="set-card">
              <h3>Players & matchmaking</h3>
              <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 14 }}>
                <div className="ss-field">
                  <span className="ss-field-label">Team format</span>
                  <GsSeg listLabel="Team format" options={['1v1', '2v2', '5v5', 'FFA', 'Custom']} value={teams} onChange={(v) => saveDoc((doc) => ({ ...doc, teams: v }))} />
                  {teams === 'Custom' && (
                    <span className="hint">Custom team sizes are defined in <Link to={editorBase + '/logic'}>Logic</Link>, not here.</span>
                  )}
                </div>
                <div className="ss-field">
                  <span className="ss-field-label">Region</span>
                  <select className="ss-input" value={region} onChange={(e) => saveDoc((doc) => ({ ...doc, region: e.target.value }))} style={{ appearance: 'none', padding: '9px 12px', fontSize: 13 }}>
                    <option>EU West</option><option disabled>US East (soon)</option><option disabled>Asia Pacific (soon)</option>
                  </select>
                </div>
              </div>
              <GsToggle label="Matchmaking" hint="Fill lobbies automatically from the player pool." on={match} onChange={(v) => saveDoc((doc) => ({ ...doc, match: v }))} />
              <GsToggle label="Skill-based matching" hint="Pair players by season rating." on={skill} onChange={(v) => saveDoc((doc) => ({ ...doc, skill: v }))} />
            </div>
          )}

          {multiplayer && (
            <div className="set-card">
              <h3>Sessions & winning</h3>
              <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr 1fr', gap: 14 }}>
                <div className="ss-field">
                  <span className="ss-field-label">Session length</span>
                  <input className="ss-input" value={sessionLength} onChange={(e) => saveDoc((doc) => ({ ...doc, sessionLength: e.target.value }))}
                    onBlur={(e) => saveDoc((doc) => ({ ...doc, sessionLength: clampNumericField(e.target.value, doc.sessionLength ?? '100s', 1) }))}
                    style={{ fontFamily: 'var(--ss-sans)', fontWeight: 500, fontSize: 13 }} />
                </div>
                <div className="ss-field">
                  <span className="ss-field-label">Wins needed</span>
                  <input className="ss-input" value={winsNeeded} onChange={(e) => saveDoc((doc) => ({ ...doc, winsNeeded: e.target.value }))}
                    onBlur={(e) => saveDoc((doc) => ({ ...doc, winsNeeded: clampNumericField(e.target.value, doc.winsNeeded ?? '7', 1) }))}
                    style={{ fontFamily: 'var(--ss-sans)', fontWeight: 500, fontSize: 13 }} />
                </div>
                <div className="ss-field">
                  <span className="ss-field-label">Win condition</span>
                  <select className="ss-input" value={winCondition} onChange={(e) => {
                    const next = e.target.value;
                    saveDoc((doc) => ({ ...doc, winCondition: next }));
                    applyWinConditionPreset(next, sessionLength, winsNeeded);
                  }} style={{ appearance: 'none', padding: '9px 12px', fontSize: 13 }}>
                    <option>Objective complete</option><option>Score target</option><option>Last standing</option><option>Race finish</option><option>Survival time</option><option>Custom (Logic rule)</option>
                  </select>
                  {(winCondition === 'Score target' || winCondition === 'Survival time') && (
                    <span className="hint">Seeded a starter rule in <Link to={editorBase + '/logic'}>Logic</Link> - tweak it there.</span>
                  )}
                  {(winCondition === 'Custom (Logic rule)' || winCondition === 'Objective complete' || winCondition === 'Last standing' || winCondition === 'Race finish') && (
                    <span className="hint">Define this rule in <Link to={editorBase + '/logic'}>Logic</Link>.</span>
                  )}
                </div>
              </div>
              <GsToggle label="Switch sides at half" hint="Teams swap roles when one reaches half the wins." on={sides} onChange={(v) => saveDoc((doc) => ({ ...doc, sides: v }))} />
            </div>
          )}

          <div className="set-card">
            <h3>Spawning</h3>
            <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 14 }}>
              <div className="ss-field">
                <span className="ss-field-label">Respawn mode</span>
                <select className="ss-input" value={storedDoc.respawnMode ?? 'Per round'} onChange={(e) => saveDoc((doc) => ({ ...doc, respawnMode: e.target.value }))} style={{ appearance: 'none', padding: '9px 12px', fontSize: 13 }}>
                  <option>Per round</option><option>Timed wave</option><option>Instant</option><option>At checkpoint</option>
                </select>
              </div>
              <div className="ss-field">
                <span className="ss-field-label">Spawn protection</span>
                <input className="ss-input" value={storedDoc.spawnProtection ?? '3s'} onChange={(e) => saveDoc((doc) => ({ ...doc, spawnProtection: e.target.value }))}
                  onBlur={(e) => saveDoc((doc) => ({ ...doc, spawnProtection: clampNumericField(e.target.value, doc.spawnProtection ?? '3s', 0) }))}
                  style={{ fontFamily: 'var(--ss-sans)', fontWeight: 500, fontSize: 13 }} />
              </div>
            </div>
            <span className="hint">Spawn points are placed in Edit mode; gameplay markers appear violet in the scene tree. Respawn mode and spawn protection save with your project; match rules apply as multiplayer hosting features land.</span>
          </div>

          <div className="set-card">
            <h3>Player character</h3>
            <div className="ss-field">
              <span className="ss-field-label">Body</span>
              <select
                className="ss-input"
                value={storedDoc.playerCharacterId ?? ''}
                onChange={(e) => saveDoc((doc) => ({ ...doc, playerCharacterId: e.target.value || undefined }))}
                style={{ appearance: 'none', padding: '9px 12px', fontSize: 13 }}
              >
                <option value="">Stock rig (default)</option>
                {playerCharacterOptions.map((c) => <option key={c.id} value={c.id}>{c.name}</option>)}
              </select>
              <Link to={editorBase + '/animation'} style={{ fontSize: 10.5, color: 'var(--ss-text-3)', marginTop: 4, display: 'inline-block' }}>
                + Rig a new body in Animation Studio
              </Link>
            </div>
            <span className="hint">The selected character stands in for the built-in body everywhere the player appears - your own view and every other player's. It needs authored idle/walk poses in Animation Studio or it renders frozen in bind pose.</span>
          </div>
        </div>
      </div>
    </div>
  );
}

/** Camera & projection card, plus its confirm dialog (docs/editor-
 * consolidation/panel-content-spec.md §10, `game.camera`) - kept isolated
 * from the flat-form grouping the other three panels use, since a
 * projection change has externally-visible blast radius (it can strand
 * whole other mode-rail entries behind a redirect). */
export function GameCameraPanel() {
  const { projectId, project } = useEditorHost();
  const { setProject } = useProject();
  const setCameraModeForced = useEngineRuntimeStore((state) => state.setCameraModeForced);
  const { data: storedDoc, setData: saveDoc } = useSharedModeDocument<GameSetupDoc>('gamesetup', GAMESETUP_DOC_FALLBACK);
  const proj = resolvedProjection(storedDoc.projection, project.map.tilemap2d?.mode);

  const setProj = async (p: string) => {
    const dim = p.startsWith('2D') ? '2D' : '3D';
    try { localStorage.setItem('ss-projection', dim); } catch { /* ignore */ }
    // Both saves used to be fire-and-forget with no .catch() - a rejected
    // request (this changes the whole studio's projection, an "externally
    // visible blast radius" change per the comment above) became an
    // unhandled promise rejection with zero feedback: the radio card would
    // look selected while nothing had actually persisted.
    try {
      await saveProjectConfig(projectId, { ...project.config, projection: dim });
    } catch (err) {
      toast('Could not save the camera/projection change: ' + (err instanceof Error ? err.message : 'unknown error'), 'error');
    }
    saveDoc((doc) => ({ ...doc, projection: p }));
    // The 2D options each imply a Tilemap2D.mode; write it through so this
    // picker and Pixel Studio's own mode toggle stay in sync on one field
    // rather than "2D side view" being a separate, decorative choice. Only
    // meaningful once a tilemap2d layer exists - Pixel Studio's own toggle
    // sets the mode a fresh layer is created with.
    const tilemapMode = tilemapModeFromProjection(p);
    if (tilemapMode && project.map.tilemap2d) {
      const nextMap = { ...project.map, tilemap2d: { ...project.map.tilemap2d, mode: tilemapMode } };
      setProject({ ...project, map: nextMap });
      try {
        await saveMapDocument(projectId, nextMap);
      } catch (err) {
        toast('Could not save the tilemap mode change: ' + (err instanceof Error ? err.message : 'unknown error'), 'error');
      }
    }
  };
  // Crossing the 2D/3D line strands whatever the creator built on the other
  // side's dimension-specific pages behind a redirect to Edit mode (nothing
  // is deleted, but it becomes unreachable through the UI until they switch
  // back) - confirm first rather than letting one radio click do that
  // silently. Switching within the same dimension (e.g. first- to
  // third-person) never touches those pages, so it stays a plain click.
  const [pendingProj, setPendingProj] = useState<string | null>(null);
  const dimOf = (p: string) => (p.startsWith('2D') ? '2D' : '3D');
  const requestProj = (p: string) => {
    if (dimOf(p) === dimOf(proj)) { void setProj(p); return; }
    setPendingProj(p);
  };
  // The creator's camera choice forces the mode for players (the live V
  // toggle is disabled while forced, see FirstPersonController.tsx) rather
  // than just setting a default: the hint below already promises this
  // choice "sets the player camera", the same way the 2D/3D half of this
  // same choice already forces the whole studio's projection.
  useEffect(() => {
    setCameraModeForced(cameraModeFromProjection(proj));
  }, [proj, setCameraModeForced]);

  return (
    <div style={{ flex: 1, overflowY: 'auto', minWidth: 0 }}>
      <div style={{ maxWidth: 760, margin: '0 auto', padding: '36px 40px 72px' }}>
        <div className="set-card">
          <h3>Camera & projection</h3>
          <GsRadioCards cols={4} value={proj} onChange={requestProj} options={[
            { id: '3D first person', hint: 'Through the character’s eyes.' },
            { id: '3D third person', hint: 'Camera follows behind.' },
            { id: '2D top down', hint: 'Flat world from above.' },
            { id: '2D side view', hint: 'Platformer style.' },
          ]} />
          <span className="hint">Sets the player camera and adapts the whole studio: the Edit viewport, Map Builder and Pixel Studio follow your choice of 2D or 3D.</span>
        </div>
      </div>
      {pendingProj && (
        <EdConfirmDialog
          title={`Switch to ${pendingProj}?`}
          body={
            dimOf(pendingProj) === '2D'
              ? 'This makes Model Studio, Characters, Animation Studio, FX & Materials and Cutscenes unreachable until you switch back to a 3D camera. Nothing already built there is deleted.'
              : 'This makes Pixel Studio unreachable until you switch back to a 2D camera. Nothing already built there is deleted.'
          }
          confirmLabel="Switch camera"
          onConfirm={() => { void setProj(pendingProj); setPendingProj(null); }}
          onCancel={() => setPendingProj(null)}
        />
      )}
    </div>
  );
}

/** Input map; Physics (docs/editor-consolidation/panel-content-spec.md
 * §10, `game.world`). */
export function GameWorldPanel() {
  const { project } = useEditorHost();
  const { data: storedDoc, setData: saveDoc } = useSharedModeDocument<GameSetupDoc>('gamesetup', GAMESETUP_DOC_FALLBACK);
  const proj = resolvedProjection(storedDoc.projection, project.map.tilemap2d?.mode);

  return (
    <div style={{ flex: 1, overflowY: 'auto', minWidth: 0 }}>
      <div style={{ maxWidth: 760, margin: '0 auto', padding: '36px 40px 72px' }}>
        <div style={{ display: 'flex', flexDirection: 'column', gap: 14 }}>
          <div className="set-card">
            <h3>Input map</h3>
            <div style={{ display: 'flex', flexDirection: 'column', gap: 9 }}>
              {([['Move', 'WASD / Stick', false], ['Interact', 'E', true], ['Primary action', 'Mouse 1 / R2', true], ['Menu', 'Esc', false]] as [string, string, boolean][]).map(([action, key, threeDOnly]) => (
                <div key={action} style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', gap: 12 }}>
                  <span style={{ fontSize: 13.5 }}>
                    {action}
                    {threeDOnly && proj.startsWith('2D') && <span style={{ marginLeft: 8, fontSize: 10.5, color: 'var(--ss-text-3)' }}>3D only</span>}
                  </span>
                  <div style={{ display: 'flex', alignItems: 'center', gap: 10 }}>
                    <span className="ss-kbd">{key}</span>
                    <button className="ss-btn ss-btn--ghost" disabled title="Creator-authored rebinding arrives with the input map runtime" style={{ padding: '4px 12px', fontSize: 11.5, borderRadius: 8, opacity: 0.45 }}>Rebind · soon</button>
                  </div>
                </div>
              ))}
            </div>
            <span className="hint">
              These are the engine bindings every game uses today. Creator-authored input maps are on the roadmap.
              {proj.startsWith('2D') && ' Interact and Primary action have no 2D top-down implementation yet; only Move is live.'}
            </span>
          </div>

          <div className="set-card">
            <h3>Physics</h3>
            <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 14 }}>
              <div className="ss-field">
                <span className="ss-field-label">Gravity</span>
                <input className="ss-input" value={storedDoc.gravity ?? '9.8'} onChange={(e) => saveDoc((doc) => ({ ...doc, gravity: e.target.value }))}
                  onBlur={(e) => saveDoc((doc) => ({ ...doc, gravity: clampNumericField(e.target.value, doc.gravity ?? '9.8', 0) }))}
                  style={{ fontSize: 13 }} />
              </div>
              <div className="ss-field">
                <span className="ss-field-label">Collision layers</span>
                <div style={{ display: 'flex', flexWrap: 'wrap', gap: 6 }}>
                  {['World', 'Players', 'Props', 'Triggers'].map((l) => (
                    <span key={l} style={{ fontSize: 11.5, fontWeight: 500, color: 'var(--ss-text-2)', border: '1px solid var(--ss-border-soft)', borderRadius: 999, padding: '4px 11px' }}>{l}</span>
                  ))}
                </div>
              </div>
            </div>
            <span className="hint">Gravity saves with your project; match rules apply as multiplayer hosting features land.</span>
          </div>
        </div>
      </div>
    </div>
  );
}

/** Localisation; Player pool (docs/editor-consolidation/panel-content-
 * spec.md §10, `game.meta`). The "is multiplayer" gate falls out of the
 * shared gamesetup document for free - no new plumbing, just a read of the
 * same `type` field game.rules writes to. */
/** The engine-bound HollowScript capabilities a project can grant or
 * withhold - every name in `PROJECT_GRANTABLE_CAPABILITY_NAMES`
 * (capabilityNames.ts, a dependency-free leaf, so listing them here pulls
 * nothing of the interpreter into the main bundle), with a creator-facing
 * label. `print`/`clock`/`random` are deliberately not offered: they are
 * always granted (see `STANDARD_CAPABILITY_NAMES`). */
const SCRIPT_CAPABILITY_OPTIONS: readonly { id: StudioCapabilityName; label: string; hint: string }[] = [
  { id: 'entities', label: 'Entities', hint: 'Spawn, move and remove objects in the running scene.' },
  { id: 'variables', label: 'Variables', hint: "Read and write this project's Logic variables." },
  { id: 'audio', label: 'Audio', hint: 'Play one-shot sounds.' },
  { id: 'logic', label: 'Logic effects', hint: 'Announce, give/take items, win/lose, quests.' },
  { id: 'input', label: 'Input', hint: 'Read which player actions are held down.' },
];

export function GameMetaPanel() {
  const { data: storedDoc, setData: saveDoc } = useSharedModeDocument<GameSetupDoc>('gamesetup', GAMESETUP_DOC_FALLBACK);
  const multiplayer = storedDoc.type !== 'Solo';
  // Undefined means "not yet configured" - every capability granted,
  // matching tier 1's own default (hollowscript-capabilities-v1.md §4).
  const grantedCapabilities = storedDoc.scriptCapabilities ?? SCRIPT_CAPABILITY_OPTIONS.map((c) => c.id);

  return (
    <div style={{ flex: 1, overflowY: 'auto', minWidth: 0 }}>
      <div style={{ maxWidth: 760, margin: '0 auto', padding: '36px 40px 72px' }}>
        <div style={{ display: 'flex', flexDirection: 'column', gap: 14 }}>
          <div className="set-card">
            <h3>Scripting capabilities</h3>
            <span className="hint">
              Which HollowScript capabilities this project&apos;s scripts (the Code workspace and Preview&apos;s Console) may
              use. Unchecking one takes effect on the next script run - a script that still declares it in <code>uses</code>{' '}
              fails at check time with a named error, before anything runs. <code>print</code>, <code>clock</code> and{' '}
              <code>random</code> are always available.
            </span>
            <div style={{ display: 'flex', flexDirection: 'column', gap: 8, marginTop: 4 }}>
              {SCRIPT_CAPABILITY_OPTIONS.map((option) => {
                const checked = grantedCapabilities.includes(option.id);
                return (
                  <label key={option.id} style={{ display: 'flex', alignItems: 'center', gap: 10, fontSize: 13, cursor: 'pointer' }}>
                    <button
                      role="switch"
                      aria-checked={checked}
                      className={'ss-switch' + (checked ? ' is-on' : '')}
                      onClick={() => {
                        const next = new Set(grantedCapabilities);
                        if (!checked) next.add(option.id);
                        else next.delete(option.id);
                        saveDoc((doc) => ({ ...doc, scriptCapabilities: SCRIPT_CAPABILITY_OPTIONS.map((c) => c.id).filter((id) => next.has(id)) }));
                      }}
                      aria-label={option.label}
                    />
                    <span style={{ display: 'flex', flexDirection: 'column' }}>
                      <span>{option.label}</span>
                      <span className="hint">{option.hint}</span>
                    </span>
                  </label>
                );
              })}
            </div>
          </div>

          <div className="set-card">
            <h3>Localisation</h3>
            <div style={{ display: 'flex', flexDirection: 'column', gap: 8 }}>
              <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', fontSize: 13 }}>
                <span>English (UK)</span>
                <span style={{ fontSize: 11.5, color: 'var(--ss-accent)' }}>Source</span>
              </div>
            </div>
            <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
              <span className="hint">Translations arrive with the localisation pipeline.</span>
              <button className="ss-btn ss-btn--ghost ss-btn--sm" disabled title="Translations arrive with the localisation pipeline" style={{ flexShrink: 0, opacity: 0.45 }}>+ Language · soon</button>
            </div>
          </div>

          {multiplayer && (
            <div className="set-card" style={{ flexDirection: 'row', display: 'flex', alignItems: 'center', justifyContent: 'space-between', gap: 16 }}>
              <div style={{ display: 'flex', flexDirection: 'column', gap: 3 }}>
                <h3>Player pool</h3>
                <span className="hint">{"This game shares your plan's player pool."}</span>
              </div>
              <Link to="/app/project/publish" className="ss-btn ss-btn--ghost ss-btn--sm" style={{ flexShrink: 0 }}>Manage hosting</Link>
            </div>
          )}
        </div>
      </div>
    </div>
  );
}
